'use client'
import {
  ContentfulMedia_default
} from "./chunk-BYRNGPWN.mjs";
import {
  createSectionItemHref,
  parseCardItem,
  resolveContentfulEventType
} from "./chunk-DJNQ4XY4.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/components/product/card/BookingType.tsx
import { BellIcon, ThunderIcon, UpRightIcon } from "@wbk/svg";
import { twMerge } from "tailwind-merge";
import { Typography } from "@wbk/ui";
import { useTranslation } from "@wbk/config";
import { jsx, jsxs } from "react/jsx-runtime";
var VARIABLES = {
  internal: {
    Icon: ThunderIcon
  },
  external: {
    Icon: UpRightIcon
  },
  offline: {
    Icon: BellIcon
  },
  sold_out: {
    Icon: ThunderIcon
  }
};
var ProductBookingType = ({ type, mini, className }) => {
  const { t } = useTranslation("contentful_common");
  if (!type || type === "internal") {
    return null;
  }
  const { Icon } = VARIABLES[type];
  if (mini) {
    const miniText = t(`contentful_common:type.${type}_mini`);
    const text = miniText === `type.${type}_mini` ? t(`contentful_common:type.${type}`) : miniText;
    return /* @__PURE__ */ jsxs("div", { className: twMerge("gap-XXS flex items-center text-xs", className), children: [
      /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4 shrink-0" }),
      /* @__PURE__ */ jsx("p", { className: "leading-none", children: text })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: twMerge("gap-S flex items-center p-2", className), children: [
    /* @__PURE__ */ jsx(Icon, { className: "h-6 w-6 shrink-0" }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(Typography, { variant: "label-M", children: t(`contentful_common:type.${type}`) }),
      /* @__PURE__ */ jsx(Typography, { className: "text-text-secondary", children: t(`contentful_common:type.${type}_desc`) })
    ] })
  ] });
};
var BookingType_default = ProductBookingType;

// src/components/product/card/Poster.tsx
import { twMerge as twMerge2 } from "tailwind-merge";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ProductItemPoster = ({ children, eventType = "", className, size = "md" }) => {
  const showBadge = /^(riyadh_season|premium|wbk(?!_not).*)$/.test(eventType);
  return /* @__PURE__ */ jsxs2("div", { className: twMerge2("rounded-S relative w-full overflow-hidden", className), children: [
    children,
    showBadge && /* @__PURE__ */ jsx2("div", { className: "ltr:rounded-tl-L rtl:rounded-tr-L bg-body absolute bottom-0 flex h-7 w-7 items-center justify-center ltr:right-0 rtl:left-0", children: /* @__PURE__ */ jsx2(
      "img",
      {
        "data-size": size,
        src: eventType === "riyadh_season" ? "/@wbk/contentful/season/riyadh_season.svg" : "/@wbk/contentful/season/premium.svg",
        className: "h-4 w-4 shrink-0 translate-y-px data-[size=sm]:h-4 data-[size=sm]:w-4 ltr:translate-x-px rtl:-translate-x-px",
        alt: ""
      }
    ) })
  ] });
};
var Poster_default = ProductItemPoster;

// src/components/product/card/Type.tsx
import { useMemo } from "react";
import { formatDate } from "@wbk/utils";
import { useRouter, useTranslation as useTranslation2 } from "@wbk/config";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var ProductItemType = ({ item }) => {
  const type = item.isStreamingEvent ? "streams" : resolveContentfulEventType(item);
  const { locale } = useRouter();
  const { t } = useTranslation2("contentful_common");
  const name = t(`contentful_common:type.${type}`);
  const date = useMemo(() => {
    if (type !== "events" || !item.schedule) return "";
    if (item.schedule.openScheduleText) {
      return item.schedule.openScheduleText;
    }
    if (!item.schedule.openDateTime) return "";
    const startDate = formatDate(item.schedule.openDateTime, locale, {
      month: "short",
      day: "2-digit"
    });
    if (!item.schedule.closeDateTime || item.schedule.openDateTime.split("T")[0] === item.schedule.closeDateTime.split("T")[0]) {
      return startDate;
    }
    const endDate = formatDate(item.schedule.closeDateTime, locale, {
      month: "short",
      day: "2-digit"
    });
    return `${startDate} - ${endDate}`;
  }, [item.schedule, locale, type]);
  return /* @__PURE__ */ jsxs3("div", { className: "text-text-secondary text-sm leading-none", children: [
    /* @__PURE__ */ jsx3("span", { children: name }),
    date && /* @__PURE__ */ jsx3("span", { children: ` \xB7 ${date}` })
  ] });
};
var Type_default = ProductItemType;

// src/components/product/card/Price.tsx
import { CurrencyPriceLabel } from "@wbk/currency";
import { useTranslation as useTranslation3 } from "@wbk/config";
import { Typography as Typography2 } from "@wbk/ui";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var ProductItemPrice = ({ item, priceClassName }) => {
  var _a;
  const { t } = useTranslation3("contentful_common");
  const discount = ((_a = item.deal) == null ? void 0 : _a.price) || item.discountedPrice || 0;
  const price = discount || item.startingPrice;
  const type = resolveContentfulEventType(item);
  if (item.isComingSoon) {
    return /* @__PURE__ */ jsx4(Typography2, { color: "text-secondary", variant: "body-XS", children: t("contentful_common:coming_soon") });
  }
  if (typeof price !== "number") return null;
  if (price <= 0) {
    return /* @__PURE__ */ jsx4(Typography2, { color: "tertiary", variant: "label-M", children: t("contentful_common:free") });
  }
  if (type === "auctions") {
    return /* @__PURE__ */ jsxs4("div", { className: "flex items-center gap-1", children: [
      /* @__PURE__ */ jsx4(Typography2, { variant: "body-S", color: "text-secondary", children: t("contentful_common:auctions.minimum_bid") }),
      item.startingPrice && /* @__PURE__ */ jsx4(CurrencyPriceLabel, { originalPrice: item.startingPrice, currency: item.currencyCode })
    ] });
  }
  return /* @__PURE__ */ jsxs4("div", { className: "flex items-center gap-1", children: [
    /* @__PURE__ */ jsx4(Typography2, { variant: "body-S", color: "text-secondary", children: item.prefix || t("contentful_common:starts_from") }),
    item.startingPrice && /* @__PURE__ */ jsx4(
      CurrencyPriceLabel,
      {
        originalPrice: item.startingPrice,
        discountPrice: price,
        currency: item.currencyCode,
        priceClassName
      }
    )
  ] });
};
var Price_default = ProductItemPrice;

// src/components/product/card/Zone.tsx
import { LiveTvIcon } from "@wbk/svg";
import { useTranslation as useTranslation4 } from "@wbk/config";
import { twMerge as twMerge3 } from "tailwind-merge";
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var ProductItemZone = ({ item, className }) => {
  var _a, _b, _c;
  const isStreaming = item.isStreamingEvent;
  const { t } = useTranslation4("contentful_common");
  return isStreaming ? /* @__PURE__ */ jsxs5(
    "div",
    {
      className: twMerge3(
        "bg-body-light/70 absolute top-0 z-10 m-2 flex h-14 w-14 flex-col items-center justify-center gap-0.5 rounded-lg text-center",
        className
      ),
      children: [
        /* @__PURE__ */ jsx5(LiveTvIcon, { className: "mx-auto h-6 w-6" }),
        /* @__PURE__ */ jsx5("p", { className: "text-xs", children: t("contentful_common:type.online") })
      ]
    }
  ) : ((_b = (_a = item.zone) == null ? void 0 : _a.zoneLogo) == null ? void 0 : _b.url) && /* @__PURE__ */ jsxs5(
    "div",
    {
      className: twMerge3(
        "bg-body-light/80 absolute top-0 z-10 m-2 flex items-center justify-center gap-2 rounded-lg p-1.5",
        className
      ),
      children: [
        /* @__PURE__ */ jsx5(
          ContentfulMedia_default,
          {
            className: "shrink-0 grow",
            media: (_c = item.zone) == null ? void 0 : _c.zoneLogo,
            width: 50,
            height: 50,
            alt: item.title || "",
            fit: "pad"
          }
        ),
        item.zone.sponsorLogo && /* @__PURE__ */ jsx5(
          ContentfulMedia_default,
          {
            className: "max-h-5 w-auto shrink-0 grow",
            media: item.zone.sponsorLogo,
            width: item.zone.sponsorLogo.width || 200,
            height: item.zone.sponsorLogo.height || 100,
            fit: "crop"
          }
        )
      ]
    }
  );
};
var Zone_default = ProductItemZone;

// src/components/product/card/Location.tsx
import { twMerge as twMerge4 } from "tailwind-merge";
import { getSafeCountryName } from "@wbk/utils";
import { useRouter as useRouter2 } from "@wbk/config";
import { jsx as jsx6 } from "react/jsx-runtime";
var ProductItemLocation = ({ location, className }) => {
  const { locale } = useRouter2();
  const countryCode = (location == null ? void 0 : location.countryCode) || "";
  const country = getSafeCountryName({ countryCode, locale });
  const city = (location == null ? void 0 : location.city) || "";
  const text = city && country ? `${city}, ${country}` : city || country;
  if (!text) return null;
  return /* @__PURE__ */ jsx6("p", { className: twMerge4("line-clamp-1 text-sm", className), children: text });
};
var Location_default = ProductItemLocation;

// src/components/product/card/AuctionStatus.tsx
import { BidIcon, CalendarIcon } from "@wbk/svg";
import { Typography as Typography3 } from "@wbk/ui";
import { useTranslation as useTranslation5 } from "@wbk/config";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var AuctionStatus = ({ auction }) => {
  const { t } = useTranslation5(["contentful_common"]);
  const isLive = auction.startAt && (/* @__PURE__ */ new Date()).getTime() > new Date(auction.startAt).getTime();
  const isEnded = auction.endAt && (/* @__PURE__ */ new Date()).getTime() > new Date(auction.endAt).getTime();
  if (isEnded) return null;
  if (isLive) {
    return /* @__PURE__ */ jsxs6("div", { className: "bg-error rounded-XS absolute top-0 z-10 m-2 flex items-center gap-1 px-1 py-1.5", children: [
      /* @__PURE__ */ jsx7(BidIcon, {}),
      /* @__PURE__ */ jsx7(Typography3, { as: "span", variant: "label-M", children: t("contentful_common:auctions.live") })
    ] });
  }
  return /* @__PURE__ */ jsxs6("div", { className: "rounded-XS absolute top-0 z-10 m-2 flex items-center gap-1 bg-[#1746CC] px-1 py-1.5", children: [
    /* @__PURE__ */ jsx7(CalendarIcon, {}),
    /* @__PURE__ */ jsx7(Typography3, { as: "span", variant: "label-M", children: t("contentful_common:auctions.upcoming") })
  ] });
};
var AuctionStatus_default = AuctionStatus;

// src/components/product/card/AuctionCountdown.tsx
import { useId } from "react";
import { useCountDown } from "@wbk/hooks";
import { Typography as Typography4 } from "@wbk/ui";
import { useTranslation as useTranslation6 } from "@wbk/config";
import { jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
var AuctionCountdown = ({ auction, mini }) => {
  var _a;
  if (auction.isStreamingEvent) {
    if (!((_a = auction.schedule) == null ? void 0 : _a.openDateTime)) return null;
    const duration2 = (new Date(auction.schedule.openDateTime).getTime() - Date.now()) / 1e3;
    return /* @__PURE__ */ jsx8(Countdown, { initialSeconds: duration2, mini });
  }
  const isLive = auction.startAt && (/* @__PURE__ */ new Date()).getTime() > new Date(auction.startAt).getTime();
  const targetDate = isLive ? auction.endAt : auction.startAt;
  if (!targetDate) return null;
  const duration = (new Date(targetDate).getTime() - Date.now()) / 1e3;
  return /* @__PURE__ */ jsx8(Countdown, { initialSeconds: duration, mini });
};
var Countdown = ({ initialSeconds, mini }) => {
  const id = useId();
  const { t } = useTranslation6(["contentful_common"]);
  const { timer, isTimeUp } = useCountDown({ id, initialSeconds });
  if (isTimeUp) return null;
  if (mini) {
    return /* @__PURE__ */ jsxs7("div", { children: [
      /* @__PURE__ */ jsx8(Typography4, { variant: "body-S", className: "text-text-secondary", children: t("contentful_common:auctions.time_left") }),
      /* @__PURE__ */ jsx8(Typography4, { variant: "label-M", children: `${timer.days}d ${timer.hours}h ${timer.minutes}m ${timer.seconds}s` })
    ] });
  }
  return /* @__PURE__ */ jsx8("div", { className: "bg-body-light/70 gap-XS absolute bottom-4 left-1/2 z-10 flex w-full max-w-[90%] -translate-x-1/2 items-center justify-center rounded-full p-2 text-center", children: ["days", "hours", "minutes", "seconds"].map((unit) => /* @__PURE__ */ jsxs7("div", { className: "flex-1 grow text-center", children: [
    /* @__PURE__ */ jsx8(Typography4, { variant: "heading-S", className: "leading-none", children: timer[unit] }),
    /* @__PURE__ */ jsx8(Typography4, { variant: "body-XS", className: "uppercase", children: t(`contentful_common:auctions.${unit}`).toUpperCase() })
  ] }, unit)) });
};
var AuctionCountdown_default = AuctionCountdown;

// src/components/product/card/Deal.tsx
import { PriceTagIcon } from "@wbk/svg";
import { useTranslation as useTranslation7 } from "@wbk/config";
import { Paper, Typography as Typography5 } from "@wbk/ui";
import { calculateDiscountPercentage } from "@wbk/utils";
import { twMerge as twMerge5 } from "tailwind-merge";
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var ProductItemDeal = ({ item, className, showLabel }) => {
  var _a, _b, _c;
  const { t } = useTranslation7("contentful_common");
  const original = Number(item.startingPrice);
  const discount = ((_a = item.deal) == null ? void 0 : _a.price) || item.discountedPrice;
  const hasDiscount = typeof discount === "number" && discount >= 0 && discount < original;
  const discountPercentage = hasDiscount ? calculateDiscountPercentage(original, discount) : void 0;
  const hasLabel = showLabel && !!((_b = item.deal) == null ? void 0 : _b.label);
  const prefix = (_c = item.deal) == null ? void 0 : _c.labelPrefix;
  const mainLabel = prefix || `${t("contentful_common:price_save")} ${discountPercentage}%`;
  if (!item.deal || !hasDiscount) {
    return null;
  }
  return /* @__PURE__ */ jsxs8(
    Paper,
    {
      className: twMerge5(
        "bg-success-900 sm:px-XS sm:py-XXS rounded-XS border-tertiary/20 gap-XXS absolute top-0 z-10 m-2 flex items-center border px-1 py-0.5",
        className
      ),
      rounded: false,
      border: false,
      children: [
        /* @__PURE__ */ jsx9(PriceTagIcon, { className: "text-tertiary h-3 w-3 sm:h-4 sm:w-4" }),
        /* @__PURE__ */ jsxs8(Typography5, { color: "tertiary", variant: "label-M", className: "leading-tight", children: [
          mainLabel,
          hasLabel && ` \u2022 ${item.deal.label}`
        ] })
      ]
    }
  );
};
var Deal_default = ProductItemDeal;

// src/components/product/card/Item.tsx
import { Button, Typography as Typography6 } from "@wbk/ui";
import { Link, useRouter as useRouter3 } from "@wbk/config";
import { useAnalytics } from "@wbk/analytics";
import { ClockIcon } from "@wbk/svg";
import { Fragment, jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var ProductItem = ({ item, cardType = "default", section, index, onClick }) => {
  var _a, _b, _c;
  const resolvedType = resolveContentfulEventType(item);
  const card = parseCardItem({ item });
  const { trackEvent } = useAnalytics();
  const { locale } = useRouter3();
  const hasTimer = resolvedType === "auctions" || !!(card == null ? void 0 : card.isStreamingEvent);
  if (!card) {
    return null;
  }
  return /* @__PURE__ */ jsx10(
    Button,
    {
      theme: "transparent",
      as: Link,
      "data-testid": `item_${resolvedType}_${card.slug}`,
      href: `/${locale}${createSectionItemHref(card)}`,
      className: "group relative h-full w-full gap-0 bg-transparent p-0",
      onClick: () => {
        var _a2;
        if (onClick) {
          onClick();
        } else {
          trackEvent({
            event: "clicked_on_section_item",
            data: {
              type: card.__typename,
              item_id: (_a2 = card.sys) == null ? void 0 : _a2.id,
              slug: card.slug,
              section: section == null ? void 0 : section.id,
              position: index
            }
          });
        }
      },
      children: /* @__PURE__ */ jsxs9("div", { className: "rounded-S bg-body flex h-full w-full flex-col items-start overflow-hidden", children: [
        /* @__PURE__ */ jsxs9(
          Poster_default,
          {
            eventType: card.eventType,
            className: cardType === "overlay" ? "h-full overflow-hidden" : "",
            children: [
              resolvedType === "auctions" ? /* @__PURE__ */ jsx10(AuctionStatus_default, { auction: (_a = card.auction) != null ? _a : {} }) : card.deal ? /* @__PURE__ */ jsx10(Deal_default, { item: card }) : /* @__PURE__ */ jsx10(Zone_default, { item: card }),
              /* @__PURE__ */ jsx10(
                ContentfulMedia_default,
                {
                  className: "aspect-1 h-full rounded-md object-cover transition-transform group-hover:scale-105",
                  media: card.image11,
                  width: 600,
                  height: 600,
                  ratio: "1x1",
                  fit: "thumb",
                  placeholder: true
                }
              ),
              hasTimer ? /* @__PURE__ */ jsx10(
                AuctionCountdown_default,
                {
                  auction: __spreadProps(__spreadValues({}, card.auction), {
                    isStreamingEvent: card.isStreamingEvent,
                    schedule: card.schedule
                  })
                }
              ) : card.deal ? /* @__PURE__ */ jsx10(Zone_default, { item: card, className: "bottom-0 top-auto" }) : null
            ]
          }
        ),
        cardType === "overlay" ? /* @__PURE__ */ jsxs9(Fragment, { children: [
          /* @__PURE__ */ jsx10("div", { className: "via-body/50 to-body/95 absolute inset-0 scale-[1.02] bg-gradient-to-b from-transparent to-85%" }),
          /* @__PURE__ */ jsxs9("div", { className: "absolute inset-0 z-20 flex flex-col justify-between p-2", children: [
            /* @__PURE__ */ jsx10("div", {}),
            /* @__PURE__ */ jsxs9("div", { className: "text-text space-y-0.5 pt-2 text-start", children: [
              /* @__PURE__ */ jsx10(Type_default, { item: card }),
              /* @__PURE__ */ jsx10(Typography6, { className: "line-clamp-2", children: card.title }),
              /* @__PURE__ */ jsx10(Location_default, { location: card.location, className: "text-text-secondary" }),
              /* @__PURE__ */ jsx10(Price_default, { item: card }),
              ((_b = card.deal) == null ? void 0 : _b.label) ? /* @__PURE__ */ jsxs9(
                Typography6,
                {
                  color: "tertiary",
                  variant: "label-S",
                  className: "gap-XXS flex items-center",
                  children: [
                    /* @__PURE__ */ jsx10(ClockIcon, { className: "h-4 w-4" }),
                    card.deal.label
                  ]
                }
              ) : /* @__PURE__ */ jsx10(BookingType_default, { className: "py-1", type: card.eventType, mini: true })
            ] })
          ] })
        ] }) : /* @__PURE__ */ jsxs9("div", { className: "text-text space-y-0.5 pt-2 text-start", children: [
          /* @__PURE__ */ jsx10(Type_default, { item: card }),
          /* @__PURE__ */ jsx10(Typography6, { className: "line-clamp-2", children: card.title }),
          /* @__PURE__ */ jsx10(Location_default, { location: card.location, className: "text-text-secondary" }),
          /* @__PURE__ */ jsx10(Price_default, { item: card }),
          ((_c = card.deal) == null ? void 0 : _c.label) ? /* @__PURE__ */ jsxs9(Typography6, { color: "tertiary", variant: "label-S", className: "gap-XXS flex items-center", children: [
            /* @__PURE__ */ jsx10(ClockIcon, { className: "h-4 w-4" }),
            card.deal.label
          ] }) : /* @__PURE__ */ jsx10(BookingType_default, { className: "py-1", type: card.eventType, mini: true })
        ] })
      ] })
    },
    card.id
  );
};
var Item_default = ProductItem;

export {
  BookingType_default,
  Poster_default,
  Type_default,
  Price_default,
  Zone_default,
  Location_default,
  AuctionStatus_default,
  AuctionCountdown_default,
  Deal_default,
  Item_default
};
//# sourceMappingURL=chunk-X26Q276J.mjs.map